import { ref, computed, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { FILTER_DEFAULTS } from '@/utils/constants';

/**
 * Modern composable for handling URL-based filter persistence
 * Provides automatic synchronization between reactive state and URL query parameters
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.defaults - Default values for filters
 * @param {Object} config.serializers - Custom serializers for complex objects
 * @param {Object} config.deserializers - Custom deserializers for complex objects
 * @param {Array} config.excludeFromUrl - Filters to exclude from URL
 * @param {number} config.debounceMs - Debounce URL updates (default: 0)
 * @param {boolean} config.watchRoute - Whether to watch for route changes (default: true)
 * @param {Function} config.onFilterChange - Callback when filters change
 */
export function useUrlFilters(config = {}) {
    const route = useRoute();

    // Configuration with defaults
    const {
        // Default values for filters
        defaults = {},
        // Custom serializers for complex objects
        serializers = {},
        // Custom deserializers for complex objects
        deserializers = {},
        // Filters to exclude from URL (sensitive or large data)
        excludeFromUrl = [],
        // Whether to update URL immediately or debounce
        debounceMs = 0,
        // Whether to watch for route changes
        watchRoute = true,
        // Callback when filters change
        onFilterChange = null
    } = config;

    // Reactive state for all filters
    const filters = ref({});

    // Track which filters have been initialized
    const initialized = ref(new Set());

    /**
     * Register a filter with the system
     * @param {string} key - Filter key
     * @param {any} defaultValue - Default value
     * @param {Object} options - Options for serialization/deserialization
     */
    const registerFilter = (key, defaultValue = null, options = {}) => {
        const {
            serialize = (value) => (typeof value === 'object' ? JSON.stringify(value) : String(value)),
            deserialize = (value) => {
                if (!value) return defaultValue;
                try {
                    return JSON.parse(value);
                } catch {
                    return value;
                }
            },
            excludeFromUrl: exclude = false
        } = options;

        // Initialize from URL or use default
        const urlValue = route.query[key];
        const initialValue = urlValue ? deserialize(urlValue) : (defaults[key] ?? defaultValue);

        filters.value[key] = initialValue;
        initialized.value.add(key);

        // Store serializers/deserializers
        if (serialize !== serializers[key]) serializers[key] = serialize;
        if (deserialize !== deserializers[key]) deserializers[key] = deserialize;
        if (exclude) excludeFromUrl.push(key);

        return computed({
            get: () => filters.value[key],
            set: (value) => {
                filters.value[key] = value;
                if (!exclude) {
                    updateUrl();
                }
            }
        });
    };

    /**
     * Get current value of a filter
     */
    const getFilter = (key) => filters.value[key];

    /**
     * Set filter value programmatically
     */
    const setFilter = (key, value) => {
        if (initialized.value.has(key)) {
            filters.value[key] = value;
            if (!excludeFromUrl.includes(key)) {
                updateUrl();
            }
        }
    };

    /**
     * Reset filter to default value
     */
    const resetFilter = (key) => {
        const defaultValue = defaults[key];
        setFilter(key, defaultValue);
    };

    /**
     * Reset all filters to defaults
     */
    const resetAllFilters = () => {
        Object.keys(filters.value).forEach((key) => {
            filters.value[key] = defaults[key];
        });
        updateUrl();
    };

    /**
     * Get current filter state as plain object
     */
    const getFilterState = () => ({ ...filters.value });

    /**
     * Update URL with current filter state
     */
    const updateUrl = () => {
        const query = { ...route.query };

        // Update each filter in URL
        Object.keys(filters.value).forEach((key) => {
            if (excludeFromUrl.includes(key)) return;

            const value = filters.value[key];
            const defaultValue = defaults[key];
            const serialize = serializers[key] || ((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)));

            // Remove from URL if it matches default value
            if (value === defaultValue || value === null || value === undefined) {
                delete query[key];
            } else {
                query[key] = serialize(value);
            }
        });

        // Create new URL with updated query parameters
        const url = new URL(window.location);
        url.search = new URLSearchParams(query).toString();

        // Update URL using History API without triggering Vue Router
        window.history.replaceState(null, '', url.toString());
    };

    /**
     * Restore filters from URL on route change
     */
    const restoreFromUrl = () => {
        Object.keys(filters.value).forEach((key) => {
            const urlValue = route.query[key];
            const deserialize =
                deserializers[key] ||
                ((value) => {
                    if (!value) return defaults[key];
                    try {
                        return JSON.parse(value);
                    } catch {
                        return value;
                    }
                });

            filters.value[key] = urlValue ? deserialize(urlValue) : defaults[key];
        });
    };

    // Watch for route changes to restore filters (optional)
    if (watchRoute) {
        watch(() => route.query, restoreFromUrl, { deep: true });
    }

    // Debounced URL update function
    let updateTimeout;
    const debouncedUpdateUrl = () => {
        if (debounceMs > 0) {
            clearTimeout(updateTimeout);
            updateTimeout = setTimeout(updateUrl, debounceMs);
        } else {
            nextTick(updateUrl);
        }
    };

    // Trigger callback when filters change
    if (onFilterChange) {
        watch(
            filters,
            (newFilters, oldFilters) => {
                if (oldFilters) {
                    onFilterChange(newFilters, oldFilters);
                }
            },
            { deep: true }
        );
    }

    return {
        // State
        filters: computed(() => filters.value),

        // Methods
        registerFilter,
        getFilter,
        setFilter,
        resetFilter,
        resetAllFilters,
        getFilterState,
        updateUrl: debouncedUpdateUrl,
        restoreFromUrl,

        // Computed helpers
        hasFilters: computed(() => Object.keys(filters.value).length > 0),
        isDefaultState: computed(() => {
            return Object.keys(filters.value).every((key) => filters.value[key] === defaults[key]);
        }),

        // Filter constants for convenience
        DEFAULTS: FILTER_DEFAULTS
    };
}

/**
 * Pre-configured composable for common date filter patterns
 */
export function useDateFilters() {
    return useUrlFilters({
        defaults: {
            dateFilter: { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H },
            timeUnit: FILTER_DEFAULTS.TIME_UNITS.HOUR
        },
        serializers: {
            dateFilter: (value) => JSON.stringify(value)
        },
        deserializers: {
            dateFilter: (value) => {
                try {
                    return JSON.parse(value);
                } catch {
                    return { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H };
                }
            }
        }
    });
}

/**
 * Pre-configured composable for pagination
 */
export function usePaginationFilters() {
    return useUrlFilters({
        defaults: {
            page: FILTER_DEFAULTS.PAGINATION.DEFAULT_PAGE,
            pageSize: FILTER_DEFAULTS.PAGINATION.DEFAULT_PAGE_SIZE
        },
        serializers: {
            page: (value) => String(value),
            pageSize: (value) => String(value)
        },
        deserializers: {
            page: (value) => parseInt(value) || FILTER_DEFAULTS.PAGINATION.DEFAULT_PAGE,
            pageSize: (value) => parseInt(value) || FILTER_DEFAULTS.PAGINATION.DEFAULT_PAGE_SIZE
        }
    });
}
